import React, { useEffect, useRef } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import styled, { keyframes } from 'styled-components';
import { Zap, Shield, Globe, Network } from 'lucide-react';

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(30px); }
  to { opacity: 1; transform: translateY(0); }
`;

const pulse = keyframes`
  0%, 100% { transform: scale(1); opacity: 0.1; }
  50% { transform: scale(1.1); opacity: 0.15; }
`;

const WhyHurrlinkWrapper = styled.section`
  position: relative;
  padding: 80px 20px;
  overflow: hidden;
  background: linear-gradient(135deg, #1e3a8a 0%, #1e40af 50%, #3730a3 100%);
  min-height: 100vh;

  @media (max-width: 768px) {
    padding: 60px 16px;
    min-height: auto;
  }

  @media (max-width: 480px) {
    padding: 50px 12px;
  }
`;

const BackgroundBlob = styled.div`
  position: absolute;
  inset: 0;
  opacity: 0.1;
  pointer-events: none;

  &::before {
    content: '';
    position: absolute;
    top: 80px;
    left: 40px;
    width: 288px;
    height: 288px;
    background: #60a5fa;
    border-radius: 50%;
    filter: blur(60px);
    animation: ${pulse} 4s ease-in-out infinite;
  }

  &::after {
    content: '';
    position: absolute;
    bottom: 80px;
    right: 40px;
    width: 384px;
    height: 384px;
    background: #a78bfa;
    border-radius: 50%;
    filter: blur(60px);
    animation: ${pulse} 4s ease-in-out infinite;
    animation-delay: 1s;
  }

  @media (max-width: 768px) {
    &::before {
      width: 200px;
      height: 200px;
      top: 40px;
      left: 20px;
    }
    &::after {
      width: 220px;
      height: 220px;
      bottom: 40px;
      right: 20px;
    }
  }
`;

const Container = styled.div`
  position: relative;
  max-width: 1024px;
  margin: 0 auto;
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 64px;
  opacity: 0;
  animation: ${fadeIn} 0.8s ease-out forwards;

  @media (max-width: 768px) {
    margin-bottom: 48px;
  }
`;

const Title = styled.h2`
  font-size: clamp(2rem, 6vw, 3.75rem);
  font-weight: 700;
  color: white;
  margin-bottom: 24px;
  letter-spacing: -0.02em;
`;

const Badge = styled.div`
  display: inline-flex;
  align-items: center;
  flex-wrap: wrap;
  justify-content: center;
  gap: 10px;
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  padding: 10px 20px;
  border-radius: 9999px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);

  span {
    color: #2563eb;
    font-weight: bold;
    font-size: 1rem;

    @media (max-width: 480px) {
      font-size: 0.9rem;
    }
  }

  .divider {
    color: #9ca3af;
  }
`;

const IntroText = styled.div`
  max-width: 896px;
  margin: 0 auto 64px;
  opacity: 0;
  animation: ${fadeIn} 0.8s ease-out 0.2s forwards;

  p {
    font-size: clamp(1rem, 3vw, 1.25rem);
    color: #dbeafe;
    line-height: 1.8;
    text-align: center;
    font-weight: 300;

    .highlight {
      font-weight: 600;
      color: white;
    }
  }

  @media (max-width: 768px) {
    margin-bottom: 48px;
  }
`;

const SectionTitle = styled.h3`
  font-size: clamp(1.5rem, 4vw, 2.25rem);
  font-weight: bold;
  color: white;
  text-align: center;
  margin-bottom: 48px;
  opacity: 0;
  animation: ${fadeIn} 0.8s ease-out 0.4s forwards;
`;

const FeaturesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 24px;
  margin-bottom: 64px;
  max-width: 1000px;
  margin-left: auto;
  margin-right: auto;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 20px;
  }

  @media (max-width: 480px) {
    gap: 16px;
  }
`;

const FeatureCard = styled.div`
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(12px);
  border-radius: 16px;
  padding: 28px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  transition: all 0.5s cubic-bezier(0.4, 0, 0.2, 1);
  opacity: 0;
  animation: ${fadeIn} 0.8s ease-out forwards;
  animation-delay: ${(props) => 0.6 + props.$index * 0.1}s;

  &:hover {
    background: rgba(255, 255, 255, 0.15);
    border-color: rgba(255, 255, 255, 0.3);
    transform: translateY(-8px);
    box-shadow: 0 20px 50px rgba(0, 0, 0, 0.3);
  }

  @media (max-width: 480px) {
    padding: 20px;
  }
`;

const FeatureContent = styled.div`
  display: flex;
  align-items: start;
  gap: 16px;

  @media (max-width: 480px) {
    gap: 12px;
  }
`;

const IconWrapper = styled.div`
  flex-shrink: 0;
  padding: 12px;
  background: rgba(59, 130, 246, 0.2);
  border-radius: 12px;
  color: #93c5fd;
  transition: all 0.3s ease;

  ${FeatureCard}:hover & {
    background: rgba(59, 130, 246, 0.3);
    color: #bfdbfe;
  }

  svg {
    width: 32px;
    height: 32px;

    @media (max-width: 480px) {
      width: 28px;
      height: 28px;
    }
  }
`;

const FeatureText = styled.div`
  flex: 1;

  h4 {
    font-size: clamp(1rem, 2.5vw, 1.25rem);
    font-weight: bold;
    color: white;
    margin-bottom: 8px;
  }

  p {
    color: rgba(219, 234, 254, 0.8);
    line-height: 1.6;
    font-size: clamp(0.85rem, 2vw, 0.95rem);
  }
`;

const WhyHurrlink = () => {
  const { t } = useTranslation('common');
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => {
      if (sectionRef.current) observer.unobserve(sectionRef.current);
    };
  }, []);

  const features = [
    { icon: <Network />, titleKey: "whyHurrlink.features.banks.title", descriptionKey: "whyHurrlink.features.banks.description" },
    { icon: <Zap />, titleKey: "whyHurrlink.features.settlement.title", descriptionKey: "whyHurrlink.features.settlement.description" },
    { icon: <Shield />, titleKey: "whyHurrlink.features.regulatory.title", descriptionKey: "whyHurrlink.features.regulatory.description" },
    { icon: <Globe />, titleKey: "whyHurrlink.features.emerging.title", descriptionKey: "whyHurrlink.features.emerging.description" }
  ];

  return (
    <WhyHurrlinkWrapper id="why-hurrlink" ref={sectionRef}>
      <BackgroundBlob />
      <Container>
        <Header>
          <Title>{t('whyHurrlink.title')}</Title>
          <Badge>
            <span>{t('whyHurrlink.badge.speed')}</span>
            <span className="divider">•</span>
            <span>{t('whyHurrlink.badge.trust')}</span>
            <span className="divider">•</span>
            <span>{t('whyHurrlink.badge.interconnection')}</span>
          </Badge>
        </Header>

        <IntroText>
          <p>
            <Trans
              t={t}
              i18nKey="whyHurrlink.intro"
              components={{ highlight: <span className="highlight" /> }}
            />
          </p>
        </IntroText>

        <SectionTitle>{t('whyHurrlink.sectionTitle')}</SectionTitle>

        <FeaturesGrid>
          {features.map((feature, index) => (
            <FeatureCard key={index} $index={index}>
              <FeatureContent>
                <IconWrapper>{feature.icon}</IconWrapper>
                <FeatureText>
                  <h4>{t(feature.titleKey)}</h4>
                  <p>{t(feature.descriptionKey)}</p>
                </FeatureText>
              </FeatureContent>
            </FeatureCard>
          ))}
        </FeaturesGrid>
      </Container>
    </WhyHurrlinkWrapper>
  );
};

export default WhyHurrlink;